// ──────────────────────────────────────────────────────────────────────
// aspectRatioMatrix.js — Vella's 13-variant platform × post_type matrix
//
// Single source of truth for aspect ratios + render dimensions per
// (platform, post_type) tuple. See Vella_Preview_Mockup_Spec.md §3 and
// Higgsfield_Prompt_Library.md §8.8.
//
// Every caller that needs an aspect ratio MUST route through
// getMatrixEntry() — NO hardcoded "1:1" / "9:16" / "16:9" / "4:5" strings
// should appear anywhere else in src/. This file is the only exception.
// ──────────────────────────────────────────────────────────────────────

/**
 * 13-variant matrix keyed by `${platform}_${post_type}` (case-sensitive).
 *
 * Keys follow the convention used in the preview spec (IG_Feed, IG_Story,
 * IG_Reel, IG_Carousel, FB_Feed, FB_Story, FB_Reel, FB_Carousel, TT_Feed,
 * LI_Feed, LI_Video, LI_Document, YT_Short, YT_Video, X_Post).
 *
 * aspect_ratio is emitted as a "W:H" string for Higgsfield / MCP calls.
 * css_aspect is the "W / H" form suitable for the CSS `aspect-ratio` prop.
 * width / height are the reference render dimensions (px).
 */
export const ASPECT_RATIO_MATRIX = {
  // ── Instagram ────────────────────────────────────────────────────────
  IG_Feed:     { aspect_ratio: "4:5",  css_aspect: "4 / 5",  width: 1080, height: 1350, preview_variant: "IG_Feed_4x5" },
  IG_Story:    { aspect_ratio: "9:16", css_aspect: "9 / 16", width: 1080, height: 1920, preview_variant: "IG_Story" },
  IG_Reel:     { aspect_ratio: "9:16", css_aspect: "9 / 16", width: 1080, height: 1920, preview_variant: "IG_Reel" },
  IG_Carousel: { aspect_ratio: "4:5",  css_aspect: "4 / 5",  width: 1080, height: 1350, preview_variant: "IG_Carousel_4x5" },

  // ── Facebook ─────────────────────────────────────────────────────────
  FB_Feed:     { aspect_ratio: "1:1",  css_aspect: "1 / 1",  width: 1080, height: 1080, preview_variant: "FB_Feed_1x1" },
  FB_Story:    { aspect_ratio: "9:16", css_aspect: "9 / 16", width: 1080, height: 1920, preview_variant: "FB_Story" },
  FB_Reel:     { aspect_ratio: "9:16", css_aspect: "9 / 16", width: 1080, height: 1920, preview_variant: "FB_Reel" },
  FB_Carousel: { aspect_ratio: "1:1",  css_aspect: "1 / 1",  width: 1080, height: 1080, preview_variant: "FB_Carousel_1x1" },

  // ── TikTok ───────────────────────────────────────────────────────────
  TT_Feed:     { aspect_ratio: "9:16", css_aspect: "9 / 16", width: 1080, height: 1920, preview_variant: "TT_Feed_9x16" },
  TT_Carousel: { aspect_ratio: "9:16", css_aspect: "9 / 16", width: 1080, height: 1920, preview_variant: "TT_Photo_Carousel" },

  // ── LinkedIn ─────────────────────────────────────────────────────────
  LI_Feed:     { aspect_ratio: "1:1",  css_aspect: "1 / 1",  width: 1080, height: 1080, preview_variant: "LI_Feed_1x1" },
  LI_Video:    { aspect_ratio: "1:1",  css_aspect: "1 / 1",  width: 1080, height: 1080, preview_variant: "LI_Video_1x1" },
  LI_Document: { aspect_ratio: "1:1",  css_aspect: "1 / 1",  width: 1080, height: 1080, preview_variant: "LI_Document_1x1" },

  // ── YouTube ──────────────────────────────────────────────────────────
  YT_Short:    { aspect_ratio: "9:16", css_aspect: "9 / 16", width: 1080, height: 1920, preview_variant: "YT_Short_9x16" },
  YT_Video:    { aspect_ratio: "16:9", css_aspect: "16 / 9", width: 1920, height: 1080, preview_variant: "YT_Video_16x9" },

  // ── X (Twitter) ──────────────────────────────────────────────────────
  X_Post:      { aspect_ratio: "16:9", css_aspect: "16 / 9", width: 1920, height: 1080, preview_variant: "X_Post_16x9" },
  X_Video:     { aspect_ratio: "16:9", css_aspect: "16 / 9", width: 1920, height: 1080, preview_variant: "X_Video_16x9" },
};

// Safe fallback — any unknown (platform, post_type) routes here so the app
// never crashes. The console.warn surfaces the miss so we can extend the
// matrix when a new variant appears.
const FALLBACK_ENTRY = Object.freeze({
  aspect_ratio: "1:1",
  css_aspect: "1 / 1",
  width: 1080,
  height: 1080,
  preview_variant: "Generic_1x1",
});

// ──────────────────────────────────────────────────────────────────────
// Platform / post-type normalization
//
// Upstream callers pass human strings ("Instagram", "Feed Post", "Reel",
// etc.); the matrix is keyed on terse 2-letter platform codes and short
// post types ("IG", "Feed"). These helpers bridge the two.
// ──────────────────────────────────────────────────────────────────────

const PLATFORM_ALIASES = {
  instagram: "IG",
  ig: "IG",
  facebook: "FB",
  fb: "FB",
  meta: "FB",
  tiktok: "TT",
  tt: "TT",
  linkedin: "LI",
  li: "LI",
  youtube: "YT",
  yt: "YT",
  x: "X",
  twitter: "X",
};

const POST_TYPE_ALIASES = {
  "feed": "Feed",
  "feed post": "Feed",
  "post": "Feed",
  "standard": "Feed",
  "story": "Story",
  "stories": "Story",
  "reel": "Reel",
  "reels": "Reel",
  "short": "Short",
  "shorts": "Short",
  "carousel": "Carousel",
  "photo carousel": "Carousel",
  "video": "Video",
  "document": "Document",
  "pdf": "Document",
};

function normalizePlatform(platform) {
  if (!platform) return null;
  const key = String(platform).trim().toLowerCase();
  return PLATFORM_ALIASES[key] || null;
}

function normalizePostType(postType, platform) {
  if (!postType) {
    // Platform-specific defaults when post_type is missing.
    if (platform === "TT") return "Feed";
    if (platform === "YT") return "Video";
    if (platform === "X") return "Post";
    return "Feed";
  }
  const key = String(postType).trim().toLowerCase();
  const mapped = POST_TYPE_ALIASES[key] || null;
  if (!mapped) return null;

  // Platform-specific post_type coercions.
  if (platform === "YT" && mapped === "Short") return "Short";
  if (platform === "YT" && mapped === "Feed") return "Video";
  if (platform === "TT" && mapped === "Reel") return "Feed";
  if (platform === "TT" && mapped === "Short") return "Feed";
  if (platform === "X" && mapped === "Feed") return "Post";
  if (platform === "X" && mapped === "Video") return "Video";
  return mapped;
}

/**
 * Look up the aspect-ratio entry for a (platform, post_type) pair.
 *
 * Safe-fallback semantics:
 *   - Unknown platform OR unknown post_type → logs a console.warn and
 *     returns the 1:1 generic fallback entry so the UI never crashes.
 *   - Known platform + missing post_type → uses a platform-default
 *     post_type (see normalizePostType).
 *
 * @param {string} platform  e.g. "Instagram" | "Facebook" | "TikTok"
 * @param {string} postType  e.g. "Feed Post" | "Story" | "Reel" | "Carousel"
 * @returns {{aspect_ratio: string, css_aspect: string, width: number, height: number, preview_variant: string}}
 */
export function getMatrixEntry(platform, postType) {
  const p = normalizePlatform(platform);
  if (!p) {
    console.warn(`[aspectRatioMatrix] Unknown platform "${platform}" — falling back to 1:1`);
    return { ...FALLBACK_ENTRY };
  }
  const pt = normalizePostType(postType, p);
  if (!pt) {
    console.warn(`[aspectRatioMatrix] Unknown post_type "${postType}" for ${p} — falling back to 1:1`);
    return { ...FALLBACK_ENTRY };
  }
  const key = `${p}_${pt}`;
  const entry = ASPECT_RATIO_MATRIX[key];
  if (!entry) {
    console.warn(`[aspectRatioMatrix] No matrix entry for "${key}" — falling back to 1:1`);
    return { ...FALLBACK_ENTRY };
  }
  return { ...entry };
}

/**
 * Convenience wrapper — return just the "W:H" string for Higgsfield calls.
 */
export function getAspectRatio(platform, postType) {
  return getMatrixEntry(platform, postType).aspect_ratio;
}

/**
 * Convenience wrapper — return just the CSS-ready "W / H" form.
 */
export function getCssAspect(platform, postType) {
  return getMatrixEntry(platform, postType).css_aspect;
}

/**
 * Return the stable variant-key (e.g. "IG_Feed_4x5") used to pick a
 * preview chrome component.
 */
export function getPreviewVariant(platform, postType) {
  return getMatrixEntry(platform, postType).preview_variant;
}

// Full list of supported variant keys — handy for enumeration / tests.
export const SUPPORTED_VARIANTS = Object.keys(ASPECT_RATIO_MATRIX);
